import React, { useState, useEffect } from 'react';
import { Currency } from '../types';

interface CalculatorProps {
  realtimePriceUSD: number;
  realtimePriceEUR: number;
}

const CurrencyToggle: React.FC<{ selected: Currency; onSelect: (c: Currency) => void; idPrefix: string }> = ({ selected, onSelect, idPrefix }) => (
  <div className="flex rounded-lg bg-dark-blue p-1">
    <button
      id={`${idPrefix}-${Currency.USD}`}
      onClick={() => onSelect(Currency.USD)}
      className={`w-full py-1 rounded-md text-sm font-semibold transition-colors ${selected === Currency.USD ? 'bg-btc-orange text-dark-blue' : 'text-light-blue hover:bg-steel-blue/50'}`}
    >
      USD
    </button>
    <button
      id={`${idPrefix}-${Currency.EUR}`}
      onClick={() => onSelect(Currency.EUR)}
      className={`w-full py-1 rounded-md text-sm font-semibold transition-colors ${selected === Currency.EUR ? 'bg-btc-orange text-dark-blue' : 'text-light-blue hover:bg-steel-blue/50'}`}
    >
      EUR
    </button>
  </div>
);

const Calculator: React.FC<CalculatorProps> = ({ realtimePriceUSD, realtimePriceEUR }) => {
  const [purchasePrice, setPurchasePrice] = useState('');
  const [purchaseCurrency, setPurchaseCurrency] = useState<Currency>(Currency.USD);
  const [quantity, setQuantity] = useState('');
  const [sellingPrice, setSellingPrice] = useState('');
  const [sellingCurrency, setSellingCurrency] = useState<Currency>(Currency.USD);
  const [capitalGainsTax, setCapitalGainsTax] = useState('');
  const [useRealtimePrice, setUseRealtimePrice] = useState(false);
  const [gainLoss, setGainLoss] = useState<number | null>(null);
  const [taxAmount, setTaxAmount] = useState<number | null>(null);

  const handlePriceInputChange = (value: string, setter: React.Dispatch<React.SetStateAction<string>>) => {
    if (value === '') {
      setter('');
      return;
    }

    // Sanitize the input to allow only numbers and one decimal point
    const sanitized = value.replace(/[^0-9.]/g, '');
    const parts = sanitized.split('.');

    if (parts.length > 2) {
      // Prevent more than one decimal point by ignoring the change
      return;
    }

    // Format the integer part with thousand separators
    const integerPart = parts[0];
    const formattedIntegerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    
    let finalValue = formattedIntegerPart;
    if (parts.length > 1) {
      // Append the decimal part if it exists
      finalValue += '.' + parts[1];
    }

    setter(finalValue);
  };

  useEffect(() => {
    if (useRealtimePrice) {
      const price = sellingCurrency === Currency.USD ? realtimePriceUSD : realtimePriceEUR;
      // Format the real-time price with thousand separators for consistency
      setSellingPrice(price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
    }
  }, [useRealtimePrice, sellingCurrency, realtimePriceUSD, realtimePriceEUR]);

  useEffect(() => {
    // Remove commas before parsing the string to a float
    const parseFormattedNumber = (val: string) => val.replace(/,/g, '');

    const pPrice = parseFloat(parseFormattedNumber(purchasePrice));
    const qty = parseFloat(quantity);
    const sPrice = parseFloat(parseFormattedNumber(sellingPrice));
    const taxRate = parseFloat(capitalGainsTax) || 0;

    if (pPrice > 0 && qty > 0 && sPrice > 0) {
      if(purchaseCurrency === sellingCurrency) {
        const grossResult = (sPrice - pPrice) * qty;
        
        if (grossResult > 0 && taxRate > 0) {
            const calculatedTax = grossResult * (taxRate / 100);
            const netResult = grossResult - calculatedTax;
            setGainLoss(netResult);
            setTaxAmount(calculatedTax);
        } else {
            setGainLoss(grossResult);
            setTaxAmount(null);
        }
      } else {
        setGainLoss(null);
        setTaxAmount(null);
      }
    } else {
      setGainLoss(null);
      setTaxAmount(null);
    }
  }, [purchasePrice, quantity, sellingPrice, purchaseCurrency, sellingCurrency, capitalGainsTax]);

  const handleRealtimeCheck = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUseRealtimePrice(e.target.checked);
    if (!e.target.checked) {
      setSellingPrice('');
    }
  };

  const getResultClasses = () => {
    if (gainLoss === null || gainLoss === 0) return 'border-steel-blue text-off-white';
    return gainLoss > 0 ? 'border-green-500 text-green-400' : 'border-red-500 text-red-400';
  };

  const getCurrencySymbol = (currency: Currency) => currency === Currency.USD ? '$' : '€';

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-base font-medium text-light-blue mb-2 tracking-wide">Purchased Price</label>
        <div className="flex items-center space-x-2">
          <input
            type="text"
            inputMode="decimal"
            value={purchasePrice}
            onChange={(e) => handlePriceInputChange(e.target.value, setPurchasePrice)}
            placeholder="e.g., 60,000"
            className="flex-grow bg-dark-blue border border-steel-blue rounded-lg p-2 text-off-white focus:ring-btc-orange focus:border-btc-orange"
          />
          <CurrencyToggle selected={purchaseCurrency} onSelect={setPurchaseCurrency} idPrefix="purchase"/>
        </div>
      </div>

      <div>
        <label htmlFor="quantity" className="block text-base font-medium text-light-blue mb-2 tracking-wide">BTC Quantity</label>
        <input
          id="quantity"
          type="number"
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          placeholder="e.g., 0.5"
          className="w-full bg-dark-blue border border-steel-blue rounded-lg p-2 text-off-white focus:ring-btc-orange focus:border-btc-orange"
        />
      </div>

      <div>
        <label className="block text-base font-medium text-light-blue mb-2 tracking-wide">Selling Price</label>
        <div className="flex items-center space-x-2">
          <input
            type="text"
            inputMode="decimal"
            value={sellingPrice}
            onChange={(e) => handlePriceInputChange(e.target.value, setSellingPrice)}
            placeholder="e.g., 70,000"
            disabled={useRealtimePrice}
            className="flex-grow bg-dark-blue border border-steel-blue rounded-lg p-2 text-off-white focus:ring-btc-orange focus:border-btc-orange disabled:opacity-50"
          />
          <CurrencyToggle selected={sellingCurrency} onSelect={setSellingCurrency} idPrefix="selling"/>
        </div>
        <div className="flex items-center mt-3">
          <input
            id="realtime-checkbox"
            type="checkbox"
            checked={useRealtimePrice}
            onChange={handleRealtimeCheck}
            className="h-4 w-4 rounded border-gray-300 text-btc-orange focus:ring-btc-orange"
          />
          <label htmlFor="realtime-checkbox" className="ml-2 block text-sm text-light-blue">
            Use real-time selling price
          </label>
        </div>
      </div>
      
       <div>
        <label htmlFor="tax-rate" className="block text-base font-medium text-light-blue mb-2 tracking-wide">Capital Gains Tax (%)</label>
        <input
          id="tax-rate"
          type="number"
          min="0"
          value={capitalGainsTax}
          onChange={(e) => setCapitalGainsTax(e.target.value)}
          placeholder="e.g., 15"
          className="w-full bg-dark-blue border border-steel-blue rounded-lg p-2 text-off-white focus:ring-btc-orange focus:border-btc-orange"
        />
      </div>
      
      {purchaseCurrency !== sellingCurrency && gainLoss === null && purchasePrice && sellingPrice && quantity && (
          <p className="text-center text-yellow-400 text-sm p-2 bg-yellow-900/50 rounded-lg">Please use the same currency for purchase and selling price to calculate gain/loss.</p>
      )}

      <div className={`p-4 border-2 rounded-lg text-center transition-colors ${getResultClasses()}`}>
        <p className="text-sm text-light-blue">Net Gain / Loss (After Tax)</p>
        <p className="text-3xl font-bold">
          {gainLoss !== null ? `${getCurrencySymbol(sellingCurrency)}${gainLoss.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : `${getCurrencySymbol(sellingCurrency)}0.00`}
        </p>
        {taxAmount !== null && taxAmount > 0 && (
          <p className="text-xs text-light-blue mt-1">
            (Tax Paid: {getCurrencySymbol(sellingCurrency)}{taxAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
          </p>
        )}
      </div>
    </div>
  );
};

export default Calculator;